import { Button } from '@/components/ui/button'
import { CheckCircle, Eye, EyeOff, Pause, Play, RotateCcw } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'

export function FocusModeToggle({
  isEnabled,
  onToggle,
}: { isEnabled: boolean; onToggle: () => void }) {
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={onToggle}
      className="gap-2"
      title={isEnabled ? 'Exit focus mode' : 'Enter focus mode'}
    >
      {isEnabled ? (
        <>
          <EyeOff className="h-4 w-4" />
          <span className="hidden sm:inline">Exit Focus</span>
        </>
      ) : (
        <>
          <Eye className="h-4 w-4" />
          <span className="hidden sm:inline">Focus Mode</span>
        </>
      )}
    </Button>
  )
}

export function FocusModeTimer({
  initialMinutes = 15,
  onComplete,
  onMarkComplete,
}: {
  initialMinutes?: number
  onComplete?: (() => void) | undefined
  onMarkComplete?: (() => void) | undefined
}) {
  const [totalSeconds] = useState(initialMinutes * 60)
  const [remainingSeconds, setRemainingSeconds] = useState(initialMinutes * 60)
  const [isRunning, setIsRunning] = useState(true)
  const [isDone, setIsDone] = useState(false)

  const minutes = Math.floor(remainingSeconds / 60)
  const seconds = remainingSeconds % 60
  const progress = ((totalSeconds - remainingSeconds) / totalSeconds) * 100

  useEffect(() => {
    if (!isRunning || remainingSeconds <= 0) return

    const interval = setInterval(() => {
      setRemainingSeconds((s) => {
        if (s <= 1) {
          setIsRunning(false)
          setIsDone(true)
          return 0
        }
        return s - 1
      })
    }, 1000)

    return () => clearInterval(interval)
  }, [isRunning, remainingSeconds])

  const handleReset = useCallback(() => {
    setRemainingSeconds(totalSeconds)
    setIsRunning(true)
    setIsDone(false)
  }, [totalSeconds])

  // Done state: show prompt instead of timer
  if (isDone) {
    return (
      <div className="fixed bottom-6 left-6 z-40">
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-background/95 backdrop-blur border shadow-lg">
          <span className="text-sm font-medium">Time's up!</span>
          <div className="flex items-center gap-2">
            {onMarkComplete && (
              <Button size="sm" onClick={onMarkComplete}>
                <CheckCircle className="h-3.5 w-3.5 mr-1" />
                Mark Complete
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={handleReset}>
              <RotateCcw className="h-3.5 w-3.5 mr-1" />
              Restart
            </Button>
            <Button size="sm" variant="ghost" onClick={onComplete}>
              Exit
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed bottom-6 left-6 z-40">
      <div className="flex items-center gap-3 px-4 py-2 rounded-full bg-background/95 backdrop-blur border shadow-lg">
        {/* Progress ring */}
        <div className="relative h-10 w-10">
          <svg
            className="h-10 w-10 -rotate-90"
            viewBox="0 0 40 40"
            role="img"
            aria-label={`${minutes}:${seconds.toString().padStart(2, '0')} remaining`}
          >
            <title>
              {minutes}:{seconds.toString().padStart(2, '0')} remaining
            </title>
            <circle
              cx="20"
              cy="20"
              r="16"
              fill="none"
              stroke="currentColor"
              strokeOpacity="0.1"
              strokeWidth="3"
            />
            <circle
              cx="20"
              cy="20"
              r="16"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeDasharray={`${(progress / 100) * 100.5} 100.5`}
              className="text-primary"
            />
          </svg>
        </div>

        {/* Time display */}
        <div className="font-mono text-lg font-semibold tabular-nums">
          {minutes}:{seconds.toString().padStart(2, '0')}
        </div>

        {/* Controls */}
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setIsRunning(!isRunning)}
            className="p-1.5 rounded-full hover:bg-muted transition-colors"
            title={isRunning ? 'Pause' : 'Resume'}
          >
            {isRunning ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
          </button>
          <button
            type="button"
            onClick={handleReset}
            className="p-1.5 rounded-full hover:bg-muted transition-colors"
            title="Reset timer"
          >
            <RotateCcw className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  )
}

// Keyboard shortcuts hook for focus mode and navigation
export function useKeyboardShortcuts({
  onNext,
  onPrevious,
  onComplete,
  onToggleFocus,
  onToggleQA,
  enabled = true,
}: {
  onNext?: () => void
  onPrevious?: () => void
  onComplete?: () => void
  onToggleFocus?: () => void
  onToggleQA?: () => void
  enabled?: boolean
}) {
  useEffect(() => {
    if (!enabled) return

    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if user is typing in an input
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        (e.target as HTMLElement).isContentEditable
      ) {
        return
      }

      switch (e.key) {
        case 'j':
        case 'ArrowRight':
          e.preventDefault()
          onNext?.()
          break
        case 'k':
        case 'ArrowLeft':
          e.preventDefault()
          onPrevious?.()
          break
        case ' ':
          if (!e.shiftKey) {
            e.preventDefault()
            onComplete?.()
          }
          break
        case 'f':
          if (!e.metaKey && !e.ctrlKey) {
            e.preventDefault()
            onToggleFocus?.()
          }
          break
        case '?':
          e.preventDefault()
          onToggleQA?.()
          break
        default:
          break
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [enabled, onNext, onPrevious, onComplete, onToggleFocus, onToggleQA])
}

// Keyboard shortcuts help tooltip
export function KeyboardShortcutsHelp() {
  const shortcuts = [
    { keys: ['j', '→'], action: 'Next module' },
    { keys: ['k', '←'], action: 'Previous module' },
    { keys: ['Space'], action: 'Mark complete' },
    { keys: ['f'], action: 'Toggle focus mode' },
    { keys: ['?'], action: 'Ask question' },
  ]

  return (
    <div className="text-xs text-muted-foreground">
      <p className="font-medium mb-2">Keyboard shortcuts</p>
      <div className="space-y-1">
        {shortcuts.map((s) => (
          <div key={s.action} className="flex items-center gap-2">
            <div className="flex items-center gap-1">
              {s.keys.map((key, i) => (
                <span key={key}>
                  {i > 0 && <span className="text-muted-foreground/50 mx-0.5">/</span>}
                  <kbd className="px-1.5 py-0.5 rounded bg-muted font-mono text-[10px]">{key}</kbd>
                </span>
              ))}
            </div>
            <span>{s.action}</span>
          </div>
        ))}
      </div>
    </div>
  )
}
